// Les téléphones par QR code, pour tous les jeux (D-167). Ce que la tablette publie de la partie (game_lives.state) et ce
// que les téléphones en lisent ; les gestes qu'un téléphone peut envoyer. Logique pure, testée.
//
// La base fait foi : un geste d'un téléphone n'existe qu'une fois écrit dans la base (game_live_act), à l'heure de la base ;
// la tablette les relit dans l'ordre et les applique avec le moteur du jeu (src/domain/parcours.ts, sablier.ts), puis
// publie la « photo » ci-dessous. Une photo ne contient RIEN de nominatif : le nom de l'équipe (sa chasuble), ses
// couleurs, sa carte, son sable ou son pion ; jamais un prénom (D-103).
import { lastActionText as pLast, canRoll, ranking, skipping, type ParcoursState } from './parcours';
import { lastActionText as sLast, sRanking, type SablierState } from './sablier';
import { readCard, type Card, type JokerFx } from './game-cards';
import { recentLog } from './game';

export type LiveKind = 'parcours' | 'sablier';
/** Les gestes d'un téléphone d'équipe ; ceux du téléphone du coach. */
export const TEAM_KINDS = ['roll', 'done', 'fail', 'shot', 'joker', 'stop'] as const;
export const COACH_KINDS = ['invalidate', 'sanction', 'change', 'pause', 'undo', 'end', 'qr'] as const;
export type TeamKind = (typeof TEAM_KINDS)[number];
export type CoachKind = (typeof COACH_KINDS)[number];
/** Le nombre de téléphones : un par équipe (six au plus) et le coach. */
export const PHONES_MAX = 7;

export interface LiveTeam {
  seat: string; name: string; color: string; ink: string; letter: string;
  card: Card | null; jokers: JokerFx[]; ok: number; ko: number; note: string | null; noteKo: boolean;
  /** Le Sablier : le sable (ms) au moment `flowFrom` (temps de jeu) ; null : arrêté. */
  res: number; flowFrom: number | null; phase: string;
  last: { title: string; at: number; ms: number; gain: number } | null;
  /** Le Parcours : la case, la case d'action en cours, les tirs, « au dé », « passe son tour ». */
  at: number; bonus: string | null; shots: boolean[]; canRoll: boolean; skip: boolean;
}
export interface LiveView {
  v: 1;
  kind: LiveKind;
  /** Numéro de la photo (croît à chaque publication). */
  seq: number;
  /** false : avant le coup d'envoi (les QR sont affichés). */
  started: boolean;
  /** L'heure de la base (ms) du temps de jeu 0, pauses comprises : temps de jeu = heure de la base − epoch. */
  epoch: number;
  /** Le temps de jeu au moment de la photo (en pause ou fini, il ne bouge plus). */
  t: number;
  paused: boolean;
  over: boolean;
  teams: LiveTeam[];
  /** Le Sablier. */
  mode: 'same' | 'own' | null;
  reserve: number;
  round: { n: number; from: number; until: number | null; card: Card } | null;
  zeroBy: string | null;
  endedBy: string | null;
  /** Le Parcours. */
  n: number;
  winner: string | null;
  ranks: { seat: string; rank: number }[];
  log: { at: number; seat: string | null; text: string }[];
  last: { text: string; at: number } | null;
}

const base = (g: { participants: { id: string; name: string; color: string; ink: string; letter: string }[]; clock: { startedAt: number; pausedAt: number | null; pausedMs: number }; over: boolean }) => ({
  epoch: g.clock.startedAt + g.clock.pausedMs,
  paused: g.clock.pausedAt !== null,
  over: g.over,
});
const team0 = (p: { id: string; name: string; color: string; ink: string; letter: string }): LiveTeam => ({
  seat: p.id, name: p.name, color: p.color, ink: p.ink, letter: p.letter, card: null, jokers: [], ok: 0, ko: 0, note: null, noteKo: false,
  res: 0, flowFrom: null, phase: '', last: null, at: 0, bonus: null, shots: [], canRoll: false, skip: false,
});

/** La photo d'une partie du Sablier, au temps de jeu `t`. */
export function sablierView(g: SablierState, o: { seq: number; t: number; reserve: number; started?: boolean }): LiveView {
  const rk = g.over ? sRanking(g, 0).map(r => ({ seat: r.id, rank: r.rank })) : [];
  return {
    v: 1, kind: 'sablier', seq: o.seq, started: o.started ?? true, ...base(g), t: o.t,
    teams: g.participants.map(p => {
      const s = g.ps[p.id]!;
      return { ...team0(p), card: s.card, jokers: s.jokers, ok: s.ok, ko: s.ko, note: s.note, noteKo: s.noteKo, res: s.res, flowFrom: s.flowFrom, phase: s.phase, last: s.last };
    }),
    mode: g.shared.rules.mode, reserve: o.reserve,
    round: g.shared.round ? { n: g.shared.round.n, from: g.shared.round.from, until: g.shared.round.until, card: g.shared.round.card } : null,
    zeroBy: g.shared.zeroBy, endedBy: g.shared.endedBy, n: 0, winner: null, ranks: rk,
    log: recentLog(g, 6).map(e => ({ at: e.at, seat: e.who, text: e.text })), last: sLast(g),
  };
}

/** La photo d'une partie du Parcours. */
export function parcoursView(g: ParcoursState, o: { seq: number; t: number; started?: boolean }): LiveView {
  return {
    v: 1, kind: 'parcours', seq: o.seq, started: o.started ?? true, ...base(g), t: o.t,
    teams: g.participants.map(p => {
      const s = g.ps[p.id]!;
      return { ...team0(p), card: s.card, jokers: s.jokers, ok: s.ok, ko: s.ko, note: s.note, noteKo: s.noteKo, phase: s.phase,
        at: s.at, bonus: s.bonus, shots: s.shots, canRoll: canRoll(g, p.id), skip: skipping(g, p.id) };
    }),
    mode: null, reserve: 0, round: null, zeroBy: null, endedBy: g.shared.endedBy, n: g.shared.rules.n, winner: g.shared.winner,
    ranks: g.over ? ranking(g).map(r => ({ seat: r.id, rank: r.rank })) : [],
    log: recentLog(g, 6).map(e => ({ at: e.at, seat: e.who, text: e.text })), last: pLast(g),
  };
}
/** La photo d'avant le coup d'envoi : les équipes, rien d'autre. */
export function lobbyView(kind: LiveKind, teams: { seat: string; name: string; color: string; ink: string; letter: string }[], o: { reserve?: number; n?: number; seq: number }): LiveView {
  return {
    v: 1, kind, seq: o.seq, started: false, epoch: 0, t: 0, paused: false, over: false,
    teams: teams.map(t => ({ ...team0({ id: t.seat, ...t }), res: (o.reserve ?? 0) * 1000 })),
    mode: null, reserve: o.reserve ?? 0, round: null, zeroBy: null, endedBy: null, n: o.n ?? 0, winner: null, ranks: [], log: [], last: null,
  };
}

// -----------------------------------------------------------------------------------------------------------------
// Côté téléphone : la photo relue sans confiance, le temps de jeu, le sable
// -----------------------------------------------------------------------------------------------------------------
const num = (v: unknown, d = 0) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const numN = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const txt = (v: unknown, max: number) => (typeof v === 'string' ? v.slice(0, max) : '');
const txtN = (v: unknown, max: number) => (typeof v === 'string' ? v.slice(0, max) : null);
const HEX = /^#[0-9a-fA-F]{6}$/;
const SEAT = /^(t[0-5]|coach)$/;

export function readLiveView(v: unknown): LiveView | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (o.v !== 1 || (o.kind !== 'parcours' && o.kind !== 'sablier') || !Array.isArray(o.teams)) return null;
  const teams: LiveTeam[] = [];
  for (const x of o.teams.slice(0, 6)) {
    if (!x || typeof x !== 'object') continue;
    const t = x as Record<string, unknown>;
    if (typeof t.seat !== 'string' || !SEAT.test(t.seat)) continue;
    const last = t.last && typeof t.last === 'object' ? t.last as Record<string, unknown> : null;
    teams.push({
      seat: t.seat, name: txt(t.name, 24), color: typeof t.color === 'string' && HEX.test(t.color) ? t.color : '#9AA1AA', ink: typeof t.ink === 'string' && HEX.test(t.ink) ? t.ink : '#FFFFFF',
      letter: txt(t.letter, 2), card: readCard(t.card), jokers: Array.isArray(t.jokers) ? t.jokers.filter((j): j is JokerFx => j === 'change' || j === 'cancel').slice(0, 6) : [],
      ok: num(t.ok), ko: num(t.ko), note: txtN(t.note, 80), noteKo: t.noteKo === true,
      res: num(t.res), flowFrom: numN(t.flowFrom), phase: txt(t.phase, 8),
      last: last ? { title: txt(last.title, 120), at: num(last.at), ms: num(last.ms), gain: num(last.gain) } : null,
      at: num(t.at), bonus: txtN(t.bonus, 8), shots: Array.isArray(t.shots) ? t.shots.map(Boolean).slice(0, 3) : [], canRoll: t.canRoll === true, skip: t.skip === true,
    });
  }
  const r = o.round && typeof o.round === 'object' ? o.round as Record<string, unknown> : null;
  const rc = r ? readCard(r.card) : null;
  return {
    v: 1, kind: o.kind, seq: num(o.seq), started: o.started !== false, epoch: num(o.epoch), t: num(o.t), paused: o.paused === true, over: o.over === true, teams,
    mode: o.mode === 'same' || o.mode === 'own' ? o.mode : null, reserve: num(o.reserve),
    round: r && rc ? { n: num(r.n), from: num(r.from), until: numN(r.until), card: rc } : null,
    zeroBy: txtN(o.zeroBy, 4), endedBy: txtN(o.endedBy, 8), n: num(o.n), winner: txtN(o.winner, 4),
    ranks: Array.isArray(o.ranks) ? o.ranks.filter((x): x is { seat: string; rank: number } => !!x && typeof x === 'object' && typeof (x as { seat: unknown }).seat === 'string').map(x => ({ seat: x.seat, rank: num(x.rank) })) : [],
    log: Array.isArray(o.log) ? o.log.slice(0, 8).filter((x): x is Record<string, unknown> => !!x && typeof x === 'object').map(x => ({ at: num(x.at), seat: txtN(x.seat, 6), text: txt(x.text, 140) })) : [],
    last: o.last && typeof o.last === 'object' ? { text: txt((o.last as Record<string, unknown>).text, 160), at: num((o.last as Record<string, unknown>).at) } : null,
  };
}

/** Le temps de jeu d'une photo à l'heure de la base `serverNow` (en pause ou fini : celui de la photo). */
export const viewTime = (v: LiveView, serverNow: number) => (!v.started || v.paused || v.over ? v.t : Math.max(v.t, serverNow - v.epoch));
/** Le sable d'une équipe, au temps de jeu `t`. */
export const viewSand = (x: LiveTeam, t: number) => Math.max(0, x.flowFrom === null ? x.res : x.res - Math.max(0, t - x.flowFrom));

/** L'écart entre l'horloge de l'appareil et celle de la base, d'après un aller-retour : on garde le plus court. */
export class ClockSync {
  offset = 0;
  private rtt = Infinity;
  /** `sent` et `recv` : l'horloge de l'appareil avant et après l'appel ; `server` : l'heure de la base rendue. */
  add(sent: number, recv: number, server: number): void {
    const rtt = Math.max(0, recv - sent);
    // Un aller-retour plus court est plus juste ; on laisse aussi vieillir la mesure (la montre de l'appareil dérive).
    if (rtt <= this.rtt * 1.5 || !Number.isFinite(this.rtt)) { this.offset = server - (sent + recv) / 2; this.rtt = Math.max(rtt, 1); }
    else this.rtt *= 1.05;
  }
  now(local = Date.now()): number { return local + this.offset; }
}

/** Un identifiant d'appareil tiré au hasard (rien d'autre : pas de compte, pas de nom). */
export function deviceId(rand: () => number = Math.random): string {
  const a = 'abcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length: 24 }, () => a[Math.floor(rand() * a.length)]).join('');
}
